import React, { useState, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { Upload, File as FileIcon, DollarSign, Printer } from 'lucide-react';
import { useAuth } from '../App.jsx';
import Select from 'react-select';
import FacturacionFormSection from '../components/FacturacionFormSection.jsx';
import RepertoriosStaffSection from '../components/Tramites/RepertoriosStaffSection.jsx';
import SeguimientoTramiteSection from '../components/Tramites/SeguimientoTramiteSection.jsx';
import { Fn } from '../utils/rutValidation.js';

const getCurrentDateTimeLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

const getBadgeClass = (estadoNombre) => {
  const txt = (estadoNombre || '').toLowerCase();
  if (txt.includes('pendiente correo')) return 'badge-warning';
  if (txt.includes('ingresado'))         return 'badge-info';
  if (txt.includes('proces'))           return 'badge-warning';
  if (txt.includes('recibido'))         return 'badge-info'; 
  if (txt.includes('pagado'))           return 'badge-success'; 
  if (txt.includes('pago'))             return 'badge-warning'; 
  if (txt.includes('complet'))         return 'badge-success';
  if (txt.includes('rechaz'))          return 'badge-error';
  if (txt.includes('reabierto'))        return 'badge-copper';
  return 'badge-gray';
};

export default function IngresoTramites() {
  const { isStaff, user } = useAuth();
  const [tab, setTab] = useState('nuevo');
  
  // -- Formulario Nuevo Trámite --
  const [form, setForm] = useState({
    rut_solicitante: '',
    nombre_solicitante: '',
    direccion: '',
    correo: '',
    telefono: '',
    tipo_inscripcion_id: '',
    area_id: '',
    observaciones: '',
    documentos: [],
    forma_entrega: 'Correo electrónico',
    envio_electronico: true,
    facturacion_misma_persona: true,
    boleta_rut: '',
    boleta_razon_social: '',
    boleta_giro: '',
    boleta_direccion: '',
    boleta_correo: '',
    _hp_website_email: '' // Campo trampa para bots (Honeypot)
  });
  
  // -- Repertorios Opcionales (Solo Funcionario)
  const [contratosList, setContratosList] = useState([]);
  const [repertoriosForms, setRepertoriosForms] = useState([]);

  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [searchParams] = useSearchParams();

  // -- Formulario Seguimiento --
  const [formSeg, setFormSeg] = useState({ rut_solicitante: '', numero_seguimiento: '' });
  const [loadingSeg, setLoadingSeg] = useState(false);
  const [resultadoSeg, setResultadoSeg] = useState(null);

  // -- Reabrir Trámite --
  const [reabrirFiles, setReabrirFiles] = useState([]);
  const [loadingReabrir, setLoadingReabrir] = useState(false);

  // -- Comentarios --
  const [comentario, setComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);

  useEffect(() => {
    loadTipos();
    const tabParam = searchParams.get('tab');
    const trackingParam = searchParams.get('tracking') || searchParams.get('seguimiento') || searchParams.get('tracking');
    const rutParam = searchParams.get('rut');

    const isSeguimientoPath = window.location.pathname.includes('/seguimiento');

    if (tabParam === 'seguimiento' || isSeguimientoPath) {
      setTab('seguimiento');
      
      const newFormSeg = {
        rut_solicitante: rutParam || formSeg.rut_solicitante,
        numero_seguimiento: trackingParam || formSeg.numero_seguimiento
      };

      if (rutParam || trackingParam) {
        setFormSeg(newFormSeg);
      }

      // Auto-búsqueda si vienen ambos parámetros
      if (rutParam && trackingParam && executeRecaptcha) {
        performSearch(newFormSeg);
      }
    }
  }, [searchParams, user, !!executeRecaptcha]);

  const loadTipos = async () => {
    try {
      const res = await api.get('/tramites/areas');
      setAreas(res.data);
      if (isStaff?.()) {
        const cRes = await api.get('/contratos');
        setContratosList(cRes.data);
      }
    } catch {
      toast.error('Error al cargar datos del formulario');
    }
  };

  const areasPrincipales = areas.filter(a => a.tipo_inscripciones && a.tipo_inscripciones.length > 0);
  const subAreas = areas.filter(a => !a.tipo_inscripciones || a.tipo_inscripciones.length === 0);

  const selectedTipo = areasPrincipales.flatMap(a => a.tipo_inscripciones).find(t => t.id === parseInt(form.tipo_inscripcion_id));
  const reqs = selectedTipo?.documentos_requeridos;

  // HANDLERS NUEVO TRÁMITE
  const normalizarRut = (r) => {
    if (!r) return r;
    let limpio = r.replace(/\./g, '').replace(/\s/g, '').toUpperCase();
    if (!limpio.includes('-') && limpio.length > 1) {
      return limpio.slice(0, -1) + '-' + limpio.slice(-1);
    }
    return limpio;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'rut_solicitante') {
      value = value.replace(/\./g, '').toUpperCase();
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar cantidad límite si es que aplica antes de dejar añadir
    if (selectedTipo?.cantidad_exacta) {
       if (form.documentos.length + files.length > selectedTipo.cantidad_exacta) {
           toast.error(`Solo puedes adjuntar exactamente ${selectedTipo.cantidad_exacta} archivo(s) para este tipo de trámite.`);
           return;
       }
    }

    setForm(prev => ({ ...prev, documentos: [...prev.documentos, ...files] }));
  };

  const removeFile = (index) => {
    setForm(prev => ({
      ...prev,
      documentos: prev.documentos.filter((_, i) => i !== index)
    }));
  };

  const handleAddRepertorioForm = () => {
    setRepertoriosForms([...repertoriosForms, {
        numero_repertorio: '',
        fecha_repertorio: getCurrentDateTimeLocal(),
        nombre_requirente: '',
        numero_contacto: '',
        contrato_id: '',
        contrato_otro: ''
    }]);
  };

  const handleRemoveRepertorioForm = (index) => {
    setRepertoriosForms(repertoriosForms.filter((_, i) => i !== index));
  };

  const handleChangeRepertorio = (index, field, value) => {
    const newF = [...repertoriosForms];
    newF[index][field] = value;
    setRepertoriosForms(newF);
  };

  const handleCopyRequirente = (index) => {
    if (index === 0) return;
    const newF = [...repertoriosForms];
    newF[index].nombre_requirente = newF[0].nombre_requirente;
    newF[index].numero_contacto = newF[0].numero_contacto;
    newF[index].fecha_repertorio = newF[0].fecha_repertorio;
    setRepertoriosForms(newF);
    toast.success("Datos copiados del primer repertorio");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Regla de Negocio: El requirente debe ser una persona natural (RUT < 50.000.000)
    if (form.rut_solicitante && Fn.esEmpresa(form.rut_solicitante)) {
      toast.error('El requirente debe ser una persona natural. Para empresas, ingrese los datos correspondientes en la sección de facturación / boleta.');
      return;
    }

    if (!form.facturacion_misma_persona) {
      if (!form.boleta_rut || !form.boleta_razon_social || !form.boleta_correo || !form.boleta_direccion) {
        toast.error('Todos los datos de facturación son obligatorios al desmarcar la casilla (RUT, Razón Social, Correo y Dirección Tributaria).');
        return;
      }
      if (!Fn.validaRut(form.boleta_rut)) {
        toast.error('El RUT de facturación / boleta es inválido.');
        return;
      }
      if (Fn.esEmpresa(form.boleta_rut) && !form.boleta_giro?.trim()) {
        toast.error('El Giro Comercial es obligatorio para empresas en los datos de facturación.');
        return;
      }
    }

    // Validar en el Front End
    if (selectedTipo?.cantidad_exacta && form.documentos.length !== selectedTipo.cantidad_exacta) {
        toast.error(`Debes subir exactamente ${selectedTipo.cantidad_exacta} archivo(s).`);
        return;
    }
    if (selectedTipo?.cantidad_minima && form.documentos.length < selectedTipo.cantidad_minima) {
        toast.error(`Debes subir al menos ${selectedTipo.cantidad_minima} archivo(s).`);
        return;
    }
    if (selectedTipo?.requiere_registro && !form.area_id) {
        toast.error(`Debes seleccionar un Registro para este trámite.`);
        return;
    }

    if (!executeRecaptcha) {
      toast.error('ReCAPTCHA no está listo aún.');
      return;
    }
    const recaptchaToken = await executeRecaptcha('ingreso_tramite');

    // Formatear el RUT antes de enviarlo
    const rutFinal = normalizarRut(form.rut_solicitante);
    setForm(prev => ({ ...prev, rut_solicitante: rutFinal }));

    const formData = new FormData();
    Object.keys(form).forEach(key => {
      if (key === 'documentos') {
        form.documentos.forEach(file => formData.append('documentos[]', file));
      } else if (key === 'rut_solicitante') {
        formData.append(key, rutFinal);
      } else {
        formData.append(key, form[key]);
      }
    });

    if (repertoriosForms.length > 0) {
       formData.append('repertorios', JSON.stringify(repertoriosForms));
    }

    setLoading(true);
    try {
      const res = await api.post('/tramites/ingresar', formData, {
        headers: { 
          'X-Recaptcha-Token': recaptchaToken,
          'Content-Type': 'multipart/form-data'
        },
      });
      if (res.data.numero_seguimiento) {
        toast.success(
          `Trámite creado: ${res.data.numero_seguimiento}`, 
          { duration: 8000, icon: '✅' }
        );
      } else {
        toast.success(res.data.message || 'Trámite ingresado. Revisa tu correo.');
      }

      setForm({ rut_solicitante: '', nombre_solicitante: '', direccion: '', correo: '', telefono: '', tipo_inscripcion_id: '', area_id: '', observaciones: '', documentos: [] });
      setTab('seguimiento');
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Error al ingresar el trámite.');
    } finally {
      setLoading(false);
    }
  };

  // HANDLERS SEGUIMIENTO
  const handleSegChange = (e) => {
    let { name, value } = e.target;
    if (name === 'rut_solicitante') {
      value = value.replace(/\./g, '').toUpperCase();
    }
    setFormSeg(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    await performSearch(formSeg);
  };

  const performSearch = async (searchData) => {
    if (!executeRecaptcha) {
      toast.error('El sistema de seguridad reCAPTCHA no está listo. Reintente en un momento.');
      return;
    }
    
    const searchDataNormalized = {
      ...searchData,
      rut_solicitante: normalizarRut(searchData.rut_solicitante)
    };

    // Actualizar la UI para que el usuario vea el RUT formateado
    if (searchData.rut_solicitante) {
       setFormSeg(prev => ({ ...prev, rut_solicitante: searchDataNormalized.rut_solicitante }));
    }

    setLoadingSeg(true);
    try {
      const recaptchaToken = await executeRecaptcha('seguimiento_tramite');
      const res = await api.post('/tramites/seguimiento', searchDataNormalized, {
        headers: { 'X-Recaptcha-Token': recaptchaToken },
      });
      setResultadoSeg(res.data.tramite);
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.error || 'Error al buscar el trámite.');
      setResultadoSeg(null);
    } finally {
      setLoadingSeg(false);
    }
  };

  const handleDownloadDoc = (doc) => {
    const url = `${api.defaults.baseURL}/tramites/documentos/${doc.token_descarga}/descargar`;
    window.open(url, '_blank');
  };

  const handleReabrirFileChange = (e) => {
    setReabrirFiles([...reabrirFiles, ...Array.from(e.target.files)]);
  };

  const submitReabrir = async () => {
    if (reabrirFiles.length === 0) return toast.error('Debe adjuntar al menos un documento para subsanar.');
    if (!executeRecaptcha) return;
    const recaptchaToken = await executeRecaptcha('reapertura_tramite');

    const formData = new FormData();
    reabrirFiles.forEach(f => formData.append('documentos[]', f));
    formData.append('rut_solicitante', formSeg.rut_solicitante);

    setLoadingReabrir(true);
    try {
      await api.post(`/tramites/${resultadoSeg.numero_seguimiento}/reabrir`, formData, {
        headers: { 'X-Recaptcha-Token': recaptchaToken, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Trámite subsanado y reabierto correctamente.');
      setReabrirFiles([]);
      // Buscar de nuevo
      handleSearch(new Event('submit'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al intentar reabrir el trámite.');
    } finally {
      setLoadingReabrir(false);
    }
  };

  const [uploadingComprobante, setUploadingComprobante] = useState(false);
  const handleSubirComprobante = async (file) => {
    if (!file) return;
    setUploadingComprobante(true);
    const fd = new FormData();
    fd.append('comprobante', file);
    fd.append('rut_solicitante', formSeg.rut_solicitante);
    fd.append('numero_seguimiento', formSeg.numero_seguimiento);

    try {
      await api.post(`/tramites/${resultadoSeg.numero_seguimiento}/comprobante`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Comprobante enviado. El cajero ha sido notificado.');
      performSearch(formSeg);
    } catch (err) {
      toast.error(err.response?.data?.errors?.comprobante?.[0] || err.response?.data?.error || err.response?.data?.message || 'Error al subir comprobante.');
    } finally {
      setUploadingComprobante(false);
    }
  };

  const handleEnviarComentario = async () => {
    if (!comentario.trim()) return;
    if (!executeRecaptcha) {
        toast.error('Por favor, espere a que el sistema de seguridad se inicialice.');
        return;
    }

    setEnviandoComentario(true);
    try {
        const token = await executeRecaptcha('enviar_comentario_tramite');
        await api.post(`/tramites/${resultadoSeg.numero_seguimiento}/comentar`, {
            comentario,
            rut_solicitante: formSeg.rut_solicitante,
            is_public: true
        }, {
            headers: { 'X-Recaptcha-Token': token }
        });
        
        toast.success('Respuesta enviada correctamente');
        setComentario('');
        performSearch(formSeg);
    } catch (error) {
        console.error(error);
        toast.error('Error al enviar la respuesta');
    } finally {
        setEnviandoComentario(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px) 4rem', maxWidth: '800px', margin: '0 auto' }}>
      
      <div className="animate-fade-in" style={{ marginBottom: '2.5rem' }}>
        <h1 className="section-title">Ingreso de Trámite en Línea</h1>
        <span className="accent-line" />
        <p className="section-subtitle">Portal para la solicitud y revisión de trámites en línea.</p>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: '2rem' }}>
        <button className={`tab ${tab === 'nuevo' ? 'active' : ''}`} onClick={() => setTab('nuevo')}>
          ➕ Nuevo Trámite
        </button>
        <button className={`tab ${tab === 'seguimiento' ? 'active' : ''}`} onClick={() => setTab('seguimiento')}>
          📍 Mis Solicitudes / Seguimiento
        </button>
      </div>

      {tab === 'nuevo' && (
        <div className="glass-card animate-scale-in" style={{ textAlign: 'left', background: 'var(--bg-surface)' }}>
          <form onSubmit={handleSubmit}>
            {/* Honeypot Field - Hidden for humans, bait for bots */}
            <div style={{ display: 'none' }} aria-hidden="true">
              <input 
                type="text" 
                name="_hp_website_email" 
                value={form._hp_website_email} 
                onChange={handleChange} 
                autoComplete="off" 
                tabIndex="-1" 
              />
            </div>

            <h3 style={{ marginBottom: '1rem', color: 'var(--primary-dark)', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>Datos del Solicitante</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="form-group mb-0">
                <label>RUT Solicitante</label>
                <input type="text" name="rut_solicitante" value={form.rut_solicitante} onChange={handleChange} placeholder="Ej: 12345678-9" required />
              </div>
              <div className="form-group mb-0">
                <label>Nombre Completo</label>
                <input type="text" name="nombre_solicitante" value={form.nombre_solicitante} onChange={handleChange} required />
              </div>
              <div className="form-group mb-0">
                <label>Correo Electrónico {isStaff?.() && '(opcional)'}</label>
                <input type="email" name="correo" value={form.correo} onChange={handleChange} required={!isStaff?.()} />
              </div>

              <div className="form-group mb-0">
                <label>Teléfono {isStaff?.() && '(opcional)'}</label>
                <input type="text" name="telefono" value={form.telefono} onChange={handleChange} required={!isStaff?.()} />
              </div>

              <div className="form-group mb-0" style={{ gridColumn: '1 / -1' }}>
                <label>Dirección {isStaff?.() && '(opcional)'}</label>
                <input type="text" name="direccion" value={form.direccion} onChange={handleChange} required={!isStaff?.()} />
              </div>

            </div>

            {/* Sección de Facturación / Boleta */}
            <FacturacionFormSection
              billingData={form}
              onChange={(newBilling) => setForm(prev => ({ ...prev, ...newBilling }))}
              solicitanteData={form}
            />

            <h3 style={{ marginBottom: '1rem', color: 'var(--primary-dark)', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>Datos del Trámite</h3>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Tipo de Inscripción / Trámite</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select name="tipo_inscripcion_id" value={form.tipo_inscripcion_id} onChange={handleChange} required style={{ flex: 1 }}>
                  <option value="">Seleccione el trámite o inscripción...</option>
                  {areasPrincipales.map(area => (
                    <optgroup key={area.id} label={`Área: ${area.nombre || 'Área'}`}>
                      {area.tipo_inscripciones?.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <div title="Seleccione el trámite correspondiente. Si no está seguro, elija la opción adecuada en la lista." style={{ cursor: 'help', fontSize: '1.2rem' }}>ℹ️</div>
              </div>
            </div>

            {selectedTipo && selectedTipo.requiere_registro && (
              <div className="form-group animate-fade-in" style={{ marginBottom: '1.5rem', background: 'rgba(var(--copper-rgb), 0.05)', padding: '1rem', borderRadius: '8px', borderLeft: '3px solid var(--copper)' }}>
                <label style={{ color: 'var(--copper)' }}>Registro o Área Requerida</label>
                <select name="area_id" value={form.area_id} onChange={handleChange} required>
                  <option value="">Seleccione el Registro Específico...</option>
                  {subAreas.map(area => (
                    <option key={area.id} value={area.id}>{area.nombre}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Adjuntar Documentos Complementarios</label>

              {(() => {
                if (reqs && reqs.length > 0) {
                  return (
                    <div className="alert alert-info" style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--info-bg)', border: '1px solid var(--info)', borderRadius: '8px' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--primary-dark)', fontSize: '0.95rem' }}>📌 Documentos Obligatorios para este Trámite:</h4>
                      <ul style={{ margin: '0 0 0.75rem 0', paddingLeft: '1.5rem', fontSize: '0.9rem', color: 'var(--text-main)' }}>
                        {reqs.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                      
                      {selectedTipo.cantidad_exacta ? (
                         <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--error)' }}>
                             Se exigen EXACTAMENTE {selectedTipo.cantidad_exacta} archivo(s).
                             {selectedTipo.documento_sugerido && ` (${selectedTipo.documento_sugerido})`}
                         </div>
                      ) : selectedTipo.cantidad_minima ? (
                         <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--warning-dark)' }}>
                             Se exige MÍNIMO {selectedTipo.cantidad_minima} archivo(s).
                             {selectedTipo.documento_sugerido && ` (${selectedTipo.documento_sugerido})`}
                         </div>
                      ) : null}
                      
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        Archivos subidos actualmente: <strong>{form.documentos.length}</strong>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="dropzone" style={{ position: 'relative', padding: '4rem 2rem', overflow: 'hidden' }}>
                <Upload size={40} style={{ color: 'var(--copper)' }} />
                <p style={{ fontSize: '1.1rem', fontWeight: 600 }}>Haz clic para subir o arrastra tus archivos aquí.</p>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Formatos soportados: (.pdf, .doc, .docx, .jpg, .png)</p>
                <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }} title="" />
              </div>
              
              {form.documentos.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, marginTop: '1rem' }}>
                  {form.documentos.map((file, i) => (
                    <li key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'var(--bg-body)', marginBottom: '0.5rem', borderRadius: '4px' }}>
                      <span style={{ fontSize: '0.9rem' }}><FileIcon size={16} style={{ display: 'inline', marginRight: '6px' }} /> {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      <button type="button" onClick={() => removeFile(i)} style={{ color: 'var(--error)', background: 'none', border: 'none', cursor: 'pointer' }}>❌</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
               <label>Observaciones y/o comentarios (Opcional)</label>
               <textarea name="observaciones" rows={4} value={form.observaciones} onChange={handleChange} placeholder="Detalles adicionales..." />
            </div>

            <h3 style={{ marginBottom: '1rem', color: 'var(--primary-dark)', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
              Modalidad de Entrega
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '1rem',
                background: form.forma_entrega === 'Correo electrónico' ? 'rgba(5, 150, 105, 0.06)' : '#fff',
                border: `2px solid ${form.forma_entrega === 'Correo electrónico' ? '#059669' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>
                <input
                  type="radio"
                  name="forma_entrega"
                  value="Correo electrónico"
                  checked={form.forma_entrega === 'Correo electrónico'}
                  onChange={() => setForm(prev => ({ ...prev, forma_entrega: 'Correo electrónico', envio_electronico: true }))}
                  style={{ marginTop: '3px' }}
                />
                <div>
                  <div style={{ fontWeight: 700, color: form.forma_entrega === 'Correo electrónico' ? '#059669' : '#0f172a' }}>
                    Correo electrónico (Digital con FEA)
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                    Se enviará a su correo con Firma Electrónica Avanzada válida legalmente.
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '1rem',
                background: form.forma_entrega === 'Retiro en oficina' ? 'rgba(2, 132, 199, 0.06)' : '#fff',
                border: `2px solid ${form.forma_entrega === 'Retiro en oficina' ? '#0284c7' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>
                <input
                  type="radio"
                  name="forma_entrega"
                  value="Retiro en oficina"
                  checked={form.forma_entrega === 'Retiro en oficina'}
                  onChange={() => setForm(prev => ({ ...prev, forma_entrega: 'Retiro en oficina', envio_electronico: false }))}
                  style={{ marginTop: '3px' }}
                />
                <div>
                  <div style={{ fontWeight: 700, color: form.forma_entrega === 'Retiro en oficina' ? '#0284c7' : '#0f172a' }}>
                    Retiro en oficina (Presencial)
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                    Retiro material impreso directamente en las dependencias del Conservador.
                  </div>
                </div>
              </label>
            </div>

            {form.forma_entrega === 'Correo electrónico' && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '0.85rem 1rem',
                marginBottom: '1.75rem',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#1e40af'
              }}>
                <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
                <div>
                  <strong>Aviso de Certificación Digital:</strong> La emisión de documentos en formato digital requiere la aplicación de Certificación Digital / Firma Electrónica Avanzada (FEA), la cual tiene un costo asociado por escritura/inmueble que se reflejará detalladamente en la cotización formal de su trámite.
                </div>
              </div>
            )}

            {isStaff?.() && (
              <RepertoriosStaffSection
                repertoriosForms={repertoriosForms}
                contratosList={contratosList}
                onAddRepertorio={handleAddRepertorioForm}
                onRemoveRepertorio={handleRemoveRepertorioForm}
                onCopyRequirente={handleCopyRequirente}
                onChangeRepertorio={handleChangeRepertorio}
              />
            )}

            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', padding: '1.2rem' }} disabled={loading}>
              {loading ? <><span className="spinner" /> Enviando...</> : 'Iniciar Trámite'}
            </button>
          </form>
        </div>
      )}

      {tab === 'seguimiento' && (
        <SeguimientoTramiteSection
          formSeg={formSeg}
          loadingSeg={loadingSeg}
          resultadoSeg={resultadoSeg}
          onSegChange={handleSegChange}
          onSearch={handleSearch}
          onDownloadDoc={handleDownloadDoc}
          onSubirComprobante={handleSubirComprobante}
          uploadingComprobante={uploadingComprobante}
          comentario={comentario}
          setComentario={setComentario}
          onEnviarComentario={handleEnviarComentario}
          enviandoComentario={enviandoComentario}
          getBadgeClass={getBadgeClass}
        />
      )}
      
    </div>
  );
}
